import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../auth/AuthContext';
import type { SelectOption } from '../../../components/inputs/selectShared';
import { routingDocumentTypesApi } from './routingApi';
import type { RoutingDocumentTypeOption } from './routingTypes';

/** The value a STAGED type holds in the picker until the form saves. */
const PENDING = 'pending:document-type';

/**
 * The document-type picker both register forms share — the live catalog, and a type the reader
 * adds from the picker STAGED until the form saves.
 *
 * **Staged, not written.** Firing the create on the press left junk rows in a catalog from forms
 * nobody saved, so the new type is created inside the form's own save, immediately before the
 * record — and as its OWN call under `routing-document-types.create`: folding it into the record's
 * create would be one key silently performing a write another governs. The residual gap is said:
 * a record the server then refuses leaves the type behind, one request wide.
 *
 * **An edit injects what the record already names** — a type deleted since — so the field states
 * what the record SAYS rather than a placeholder; the server refuses only a fresh pick of one.
 */
export function useDocumentTypeField(initial: { id: string; name: string } | null) {
  const canCreate = usePermission('routing-document-types.create');

  const [types, setTypes] = useState<RoutingDocumentTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(initial?.id ?? '');
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    routingDocumentTypesApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setTypes(rows);
      })
      // A failed feed leaves the picker empty; the form's own refusal names the field.
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const options = useMemo<SelectOption[]>(() => {
    const live = types.map((t) => ({ value: t.id, label: t.name }));
    const recorded = initial && !types.some((t) => t.id === initial.id) ? [{ value: initial.id, label: initial.name }] : [];
    const staged = pending ? [{ value: PENDING, label: pending }] : [];

    return [...staged, ...recorded, ...live];
  }, [types, initial, pending]);

  return {
    options,
    loading,
    value,
    /** The live type ids — what Autogenerate picks from. */
    liveIds: types.map((t) => t.id),
    canCreate,
    /** Whether the value is a real row or a staged name — both answer the question. */
    answered: value !== '',
    onChange: (next: string) => {
      setValue(next);

      // Picking a real row drops the staged name, or the save would create a type the reader has
      // since changed their mind about.
      if (next !== PENDING) setPending(null);
    },
    onCreateOption: canCreate
      ? (raw: string) => {
          setPending(raw.trim());
          setValue(PENDING);
        }
      : undefined,
    set: setValue,

    /**
     * The id to send — creating the staged type FIRST, under its own key. Throws what the create
     * throws, so the form reports it in its own strip.
     */
    resolve: async (): Promise<string | null> => {
      if (value !== PENDING || pending === null) {
        return value || null;
      }

      const created = await routingDocumentTypesApi.create({ name: pending, description: null });

      setTypes((current) => [...current, { id: created.id, name: created.name }]);
      setPending(null);
      setValue(created.id);

      return created.id;
    },
  };
}
